import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  createColumnHelper, getCoreRowModel, useReactTable,
} from '@tanstack/react-table';

import { getEmployeesOwnedDocumentEmployees, sendDocumentReminder, changeReviewStatusDocument } from '../../../api';
import { showDocumentPreview } from '../../../store/documents/actions';
import useNotify from '../../../hooks/useNotify';

import styles from './styles.module.scss';

import Table from '../../Core/Table';
import { BackIcon } from '../Icons';
import DocumentUploadFiles from '../DocumentUploadFiles';

const columnHelper = createColumnHelper();

const formatDate = (date) => (
  date && moment(date).isValid() ? moment(date).format('MMM D, YYYY') : '—'
);

const getInitials = (employee = {}) => (
  `${(employee.name || '').charAt(0)}${(employee.surname || '').charAt(0)}`.toUpperCase()
);

const getStatusClass = (status, classes) => {
  const statusValue = (status || '').toLowerCase();
  if (statusValue.includes('complete')) return classes.statusComplete;
  if (statusValue.includes('review')) return classes.statusReview;
  if (statusValue.includes('renewal')) return classes.statusRenewal;
  if (statusValue.includes('replacement') || statusValue.includes('expired')) return classes.statusError;
  return classes.statusMissing;
};

const EmployeeOwnedDocument = ({ documentId }) => {
  const { id: companyId } = useParams();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [uploadEmployee, setUploadEmployee] = useState(null);

  const [data, setData] = useState({
    document: {
      document_type: {},
    },
    stats: {},
    data: [],
  });

  useEffect(() => {
    const handleDocumentsUpdated = () => {
      fetchData()
    }
    window.addEventListener('documents-updated', handleDocumentsUpdated)
    return () => {
      window.removeEventListener('documents-updated', handleDocumentsUpdated)
    }
  }, [])

  useEffect(() => {
    if (companyId && documentId) {
      fetchData();
    }
  }, [companyId, documentId]);

  const fetchData = async () => {
    setLoading(true);
    const res = await getEmployeesOwnedDocumentEmployees(companyId, documentId, {});
    if (res) {
      setData({
        ...res,
        document: res.document || {},
        stats: res.stats || {},
        data: Array.isArray(res.data) ? res.data : [],
      });
      setRowSelection({});
    }
    setLoading(false);
  };

  const handleOpenDocument = useCallback((row) => {
    dispatch(showDocumentPreview({ documentId, employeeId: row.employee.id }));
  }, [dispatch, documentId]);

  const handleRemind = useCallback(async (employeeId) => {
    const res = await sendDocumentReminder(companyId, [{ document_id: documentId, employee_id: employeeId }]);
    if (res?.data?.sent?.length) {
      notify.show(t('Reminder sent successfully'));
    }
  }, [companyId, documentId, notify, t]);

  const handleUploadFiles = useCallback((row) => {
    setUploadEmployee(row.employee);
  }, []);

  const handleUploadSuccess = useCallback(async () => {
    const updatedData = await getEmployeesOwnedDocumentEmployees(companyId, documentId, {});
    if (updatedData) {
      setData({
        ...updatedData,
        document: updatedData.document || {},
        stats: updatedData.stats || {},
        data: Array.isArray(updatedData.data) ? updatedData.data : [],
      });
    }
    window.dispatchEvent(new CustomEvent('documents-updated', { detail: {} }));
  }, [companyId, documentId]);

  const closeUploadForm = useCallback(() => {
    setUploadEmployee(null);
  }, []);

  const handleAccept = async (row) => {
    const res = await changeReviewStatusDocument(companyId, documentId, {
      review_status: 'approved',
      document_employee_file_ids: row.document_employee_file_ids,
    })
    if (res) {
      fetchData()
      window.dispatchEvent(
        new CustomEvent('documents-updated', {detail: {}})
      )
    }
  }

  const handleReject = async (row) => {
    const res = await changeReviewStatusDocument(companyId, documentId, {
      review_status: 'rejected',
      document_employee_file_ids: row.document_employee_file_ids,
    })
    if (res) {
      fetchData()
      window.dispatchEvent(
        new CustomEvent('documents-updated', {detail: {}})
      )
    }
  }

  const columns = useMemo(() => [
    // columnHelper.display({
    //   id: 'select',
    //   enableSorting: false,
    //   header: ({ table }) => (
    //     <Checkbox
    //       checked={table.getIsAllRowsSelected()}
    //       onChange={table.getToggleAllRowsSelectedHandler()}
    //       aria-label={t('Select all employees')}
    //     />
    //   ),
    //   cell: ({ row }) => (
    //     <Checkbox
    //       checked={row.getIsSelected()}
    //       onChange={row.getToggleSelectedHandler()}
    //       aria-label={t('Select employee')}
    //     />
    //   ),
    // }),
    columnHelper.display({
      id: 'employee',
      header: t('Employee'),
      enableSorting: false,
      cell: ({ row }) => {
        const employee = row.original.employee || {};
        const employeeName = employee.full_name
          || `${employee.name || ''} ${employee.surname || ''}`.trim()
          || '—';
        return (
          <div className={styles.employeeCell}>
            {employee.avatar ? (
              <img
                className={styles.avatar}
                src={`${window.location.origin}/storage/${employee.avatar}`}
                alt=''
              />
            ) : (
              <div className={styles.avatar}>{getInitials(employee)}</div>
            )}
            <div className={styles.employeeInfo}>
              <div className={styles.employeeName}>{employeeName}</div>
              {employee.place?.name ? (
                <div className={styles.employeePlace}>{employee.place.name}</div>
              ) : null}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      cell: ({ row }) => {
        const { status = {} } = row.original;
        return (
          <div className={styles.statusCell}>
            <span className={`${styles.statusBadge} ${getStatusClass(status.value, styles)}`}>
              <span className={styles.statusDot} />
              {t(status.label || status.value || 'Unknown')}
            </span>
            {status.is_overdue ? <span className={styles.overdue}>{t('Overdue')}</span> : null}
            {status.description ? (
              <div className={styles.statusDescription}>{t(status.description)}</div>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'provided',
      header: t('Provided'),
      enableSorting: false,
      cell: ({ row }) => {
        const { provided } = row.original;
        return provided ? (
          <div className={styles.dateCell}>
            <span>{formatDate(provided.date)}</span>
            {provided.by_label ? <span className={styles.dateNote}>{t(provided.by_label)}</span> : null}
          </div>
        ) : <span className={styles.mutedText}>—</span>;
      },
    }),
    columnHelper.display({
      id: 'expires',
      header: t('Expires'),
      enableSorting: false,
      cell: ({ row }) => {
        const { expires } = row.original;
        if (!expires) return <span className={styles.mutedText}>—</span>;
        const expired = expires.is_expired;
        return (
          <div className={`${styles.dateCell} ${expired ? styles.expiredDate : ''}`}>
            <span>{formatDate(expires.date)}</span>
            {expires.label ? <span className={styles.dateNote}>{t(expires.label)}</span> : null}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'due_date',
      header: t('Due date'),
      enableSorting: false,
      cell: ({ row }) => {
        const dueDate = row.original.due_date || data.document.due_date_for_action;
        const overdue = row.original.status?.is_overdue;
        return (
          <span className={overdue ? styles.expiredDate : styles.mutedText}>
            {formatDate(dueDate)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('Action'),
      enableSorting: false,
      cell: ({ row }) => {
        const employeeId = row.original.employee?.id;
        const actions = Array.isArray(row.original.actions) ? row.original.actions : [];
        if (!actions.length) return <span className={styles.mutedText}>—</span>;
        return (
          <div className={styles.actions}>
            {actions.map((action) => {
              switch (action.type) {
                case 'remind':
                  return (
                    <button
                      key={action.type}
                      type='button'
                      className={styles.secondaryAction}
                      onClick={(e) => { e.stopPropagation(); handleRemind(employeeId); }}
                    >
                      <svg className={styles.bellIcon} viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                        <path
                          d='M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M10 21a2 2 0 0 0 4 0'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      {t(action.label)}
                    </button>
                  );
                case 'upload':
                  return (
                    <button
                      key={action.type}
                      type='button'
                      className={styles.uploadAction}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUploadFiles(row.original);
                      }}
                    >
                      <span className={styles.uploadIcon} aria-hidden='true'>↥</span>
                      {t(action.label)}
                    </button>
                  );
                case 'accept':
                case 'reject': {
                  let actionClass = styles.rejectAction;
                  if (action.type === 'accept') actionClass = styles.primaryAction;
                  return (
                    <button key={action.type} type='button' className={actionClass} onClick={(e) => {
                      e.stopPropagation();
                      if (action.type === 'accept') handleAccept(row.original)
                      if (action.type === 'reject') handleReject(row.original)
                    }}>
                      {t(action.label)}
                    </button>
                  );
                }
                default:
                  return null;
              }
            })}
          </div>
        );
      },
    }),
  ], [data.document.due_date_for_action, handleOpenDocument, handleRemind, handleUploadFiles, t]);

  const table = useReactTable({
    data: data.data,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => String(row.document_employee_id || row.employee?.id),
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          type='button'
          className={styles.backButton}
          aria-label={t('Go back')}
          onClick={history.goBack}
        >
          <BackIcon />
        </button>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{data.document.name || t('Document')}</div>
          <div className={styles.headerDesc}>
            {data.document.document_type?.name || data.document.category?.name}
            {data.document.due_date_for_action
              ? ` · ${t('due {{date}}', { date: formatDate(data.document.due_date_for_action) })}`
              : ''}
            {data.document.employee_count != null
              ? ` · ${t('{{count}} employees', { count: data.document.employee_count })}`
              : ''}
          </div>
        </div>
      </div>
      <div className={styles.content}>
        <Table
          table={table}
          columns={columns}
          loading={loading}
          placeholder={t('No employees found')}
          headerStyles={{ padding: '6px 10px' }}
          onClickRow={handleOpenDocument}
        />
      </div>
      {uploadEmployee ? (
        <DocumentUploadFiles
          companyId={companyId}
          documentId={documentId}
          employee={uploadEmployee}
          onClose={closeUploadForm}
          onUploaded={handleUploadSuccess}
        />
      ) : null}
    </div>
  );
};

export default EmployeeOwnedDocument;
